import { useCallback, useRef } from "react"
import { View } from "react-native"
import { router } from "expo-router"
import ConnectIcon from "@/assets/clipart/myleads-connect.svg"
import { PrimaryDarkButton } from "@/components/buttons/CommonButtons"
import { MYLEADS_GOOGLE_SCOPES } from "@/constants/Scopes"
import { BFColors } from "@/constants/Colors"
import {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetModal,
} from "@gorhom/bottom-sheet"
import { useGoogleLoginGmailMutation } from "@/queries/user"
import { setIsMyLeadsRunning } from "@/stores/myLeads"
import {
  myLeadsOnboardingViewed,
  myLeadsSetupClicked,
} from "@/analytics/events/properties"
import { useViewedCallback } from "@/analytics/react"
import { H2, Subtitle } from "@/components/standard/BFText"
import useBrowserInfo from "@/hooks/useBrowserInfo"
import { type MyLeadsConnectViewProps } from "@/components/properties/myleads/MyLeadsConnectData"
import { ConnectPopover } from "@/components/properties/myleads/MyLeadsConnectPopover"

export default function MyLeadsConnectView({
  onDismiss,
  darkTheme = false,
}: MyLeadsConnectViewProps) {
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const googleLoginGmailMutation = useGoogleLoginGmailMutation()
  const { isEmbeddedBrowser } = useBrowserInfo()

  useViewedCallback(() => {
    myLeadsOnboardingViewed()
  }, [])

  const onStart = useCallback(() => {
    // EN-4481
    if (isEmbeddedBrowser) {
      // push to app install redirect
      router.push("/properties/myleads/redirect")
    }

    bottomSheetModalRef.current?.present()
    myLeadsSetupClicked()
  }, [])

  const onSuccess = (code: string | null, redirectUri?: string) => {
    if (code) {
      googleLoginGmailMutation.mutate(
        {
          serviceType: "gmail",
          code,
          scopes: MYLEADS_GOOGLE_SCOPES,
          redirectUri,
        },
        {
          onSuccess: (data) => {
            if (data?.googleLogin?.token) {
              setIsMyLeadsRunning(true)
              if (onDismiss) {
                onDismiss()
              } else {
                bottomSheetModalRef?.current?.dismiss()
              }
              router.push("/properties/myleads/success")
            }
          },
          onError: (error: Error) => {
            console.error(error)
          },
        }
      )
    }
  }

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
      />
    ),
    []
  )

  return (
    <View className="flex flex-col flex-1 gap-10">
      <View className="flex-1 flex-col p-6 gap-2">
        <H2 className="text-teal">Never miss the perfect deal</H2>
        <Subtitle className={"mt-4 text-white"}>
          Connect your email, set your buy box and Backflip will automatically
          highlight your best new deals right in your app.
        </Subtitle>
        <View className="flex-1 items-center ml-[-32px] pt-10">
          <ConnectIcon />
        </View>

        <PrimaryDarkButton
          testID={"my-leads-button"}
          title="I Want My Leads!"
          onPress={onStart}
        />
      </View>

      {!isEmbeddedBrowser && (
        <BottomSheetModal
          ref={bottomSheetModalRef}
          snapPoints={["70%"]}
          backdropComponent={renderBackdrop}
          index={0}
          backgroundStyle={{ backgroundColor: BFColors.calmBlue }}
          handleIndicatorStyle={{ backgroundColor: "#FFFFFF" }}
          enableDynamicSizing={false}
        >
          <ConnectPopover
            onSuccess={onSuccess}
            onDismiss={() => {
              bottomSheetModalRef?.current?.dismiss()
            }}
          />
        </BottomSheetModal>
      )}
    </View>
  )
}
